/**
 * Unified portal authentication hook.
 *
 * Reads portal auth state from localStorage (populated by the magic-link
 * exchange) and handles proactive token refresh.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getPortalAuth,
  clearPortalAuth,
  onPortalAuthChange,
} from '../storage';
import { validatePortalToken, shouldRefreshToken } from '../tokenUtils';
import { attemptTokenRefresh } from '../refreshToken';
import { PORTAL_TOKEN_CONFIG } from '@/config/auth';
import type { PortalRole } from '../types';

export interface UsePortalAuthOptions {
  /**
   * Client ID for client-specific login.
   * If not provided, uses generic login endpoint.
   */
  clientId?: string;

  /**
   * Expected role for validation.
   */
  role?: PortalRole;

  /**
   * Skip session restore on mount.
   */
  skipRestore?: boolean;
}

export interface PortalAuthResult {
  isAuthenticated: boolean;
  isValidating: boolean;
  isCheckingSession: boolean;
  token: string | null;
  role: string | null;
  clientName: string | null;
  clientId: string | null;
  logout: () => void;
  refresh: () => Promise<boolean>;
}

export function usePortalAuth(options: UsePortalAuthOptions = {}): PortalAuthResult {
  const { clientId: initialClientId, skipRestore = false } = options;

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(!skipRestore);
  const isValidating = false;
  const [token, setToken] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(initialClientId || null);

  // Restore session from localStorage on mount
  useEffect(() => {
    if (skipRestore || !initialClientId) {
      setIsCheckingSession(false);
      return;
    }

    // Re-arm the checking flag: useState's initial value only applies on
    // mount, so on a clientId change (client switcher, /client/:a ->
    // /client/:b navigation) the flag would otherwise stay false while
    // the awaited refresh below is in flight — re-opening the
    // unauthenticated render window this hook exists to close.
    setIsCheckingSession(true);

    // Guards state writes after unmount or a clientId change. Without it
    // the awaited refresh below could write stale auth into a remounted
    // hook instance.
    let cancelled = false;

    const restoreAuth = async () => {
      try {
        console.log('[usePortalAuth] Restoring auth for client:', initialClientId);
        const stored = getPortalAuth(initialClientId);

        if (stored) {
          // Validate token before restoring
          const validation = validatePortalToken(stored.token, initialClientId);

          if (validation.valid) {
            setToken(stored.token);
            setRole(stored.role);
            setClientName(stored.clientName || null);
            setClientId(initialClientId);
            setIsAuthenticated(true);
            console.log('[usePortalAuth] Restored token for client:', initialClientId, 'role:', stored.role);

            // Proactive refresh if token is near expiry. Fire-and-forget
            // is fine here: the session is already authenticated, so the
            // UI never sees an unauthenticated window.
            if (shouldRefreshToken(stored.token, PORTAL_TOKEN_CONFIG.refreshThresholdMinutes)) {
              console.log('[usePortalAuth] Token near expiry, refreshing proactively');
              attemptTokenRefresh(initialClientId).then((result) => {
                if (!cancelled && result.success && result.token) {
                  setToken(result.token);
                }
              });
            }
          } else if (validation.reason === 'expired' && stored.refreshToken) {
            // Access token expired but we have a refresh token -- try to
            // recover. MUST be awaited: releasing isCheckingSession while
            // the refresh is in flight leaves an unauthenticated window,
            // and ApprovalView bounces to /portal/login, whose stored-
            // session short-circuit bounces straight back — the login
            // page flashes in a loop until the refresh lands.
            console.log('[usePortalAuth] Access token expired, attempting refresh');
            const result = await attemptTokenRefresh(initialClientId);
            if (cancelled) return;
            if (result.success && result.token) {
              const refreshedAuth = getPortalAuth(initialClientId);
              setToken(result.token);
              setRole(refreshedAuth?.role || stored.role);
              setClientName(refreshedAuth?.clientName || stored.clientName || null);
              setClientId(initialClientId);
              setIsAuthenticated(true);
              console.log('[usePortalAuth] Token refreshed on restore');
            } else {
              console.log('[usePortalAuth] Refresh failed, clearing auth');
              clearPortalAuth(initialClientId);
            }
          } else {
            console.log('[usePortalAuth] Stored token invalid:', validation.reason);
            clearPortalAuth(initialClientId);
          }
        } else {
          console.log('[usePortalAuth] No stored token for client:', initialClientId);
        }
      } catch (error) {
        console.error('[usePortalAuth] Auth restore failed:', error);
      } finally {
        if (!cancelled) {
          setIsCheckingSession(false);
        }
      }
    };

    restoreAuth();

    return () => {
      cancelled = true;
    };
  }, [initialClientId, skipRestore]);

  // Cross-tab sync: react to storage changes from other tabs
  useEffect(() => {
    if (!initialClientId) return;

    const unsubscribe = onPortalAuthChange(initialClientId, (data) => {
      if (!data) {
        console.log('[usePortalAuth] Auth cleared in another tab, logging out');
        setIsAuthenticated(false);
        setToken(null);
        setRole(null);
        setClientName(null);
        return;
      }

      const validation = validatePortalToken(data.token, initialClientId);
      if (validation.valid) {
        console.log('[usePortalAuth] Auth updated in another tab, syncing');
        setToken(data.token);
        setRole(data.role);
        setClientName(data.clientName || null);
        setClientId(initialClientId);
        setIsAuthenticated(true);
      }
    });

    return unsubscribe;
  }, [initialClientId]);

  // Periodic refresh check (every 60s while authenticated)
  useEffect(() => {
    if (!isAuthenticated || !initialClientId || !token) return;

    const interval = setInterval(() => {
      if (shouldRefreshToken(token, PORTAL_TOKEN_CONFIG.refreshThresholdMinutes)) {
        console.log('[usePortalAuth] Periodic refresh triggered');
        attemptTokenRefresh(initialClientId).then((result) => {
          if (result.success && result.token) {
            setToken(result.token);
          }
        });
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, [isAuthenticated, initialClientId, token]);

  const refresh = useCallback(async (): Promise<boolean> => {
    const targetClientId = clientId || initialClientId;
    if (!targetClientId) return false;

    const result = await attemptTokenRefresh(targetClientId);
    if (result.success && result.token) {
      setToken(result.token);
      return true;
    }
    return false;
  }, [clientId, initialClientId]);

  const logout = useCallback(() => {
    const targetClientId = clientId || initialClientId;
    console.log('[usePortalAuth] Logging out:', targetClientId);

    if (targetClientId) {
      clearPortalAuth(targetClientId);
    }

    setIsAuthenticated(false);
    setToken(null);
    setRole(null);
    setClientName(null);

    console.log('[usePortalAuth] Logout complete');
  }, [clientId, initialClientId]);

  return {
    isAuthenticated,
    isValidating,
    isCheckingSession,
    token,
    role,
    clientName,
    clientId,
    logout,
    refresh,
  };
}
